/* eslint-disable relay/unused-fields */
import type { NextPageContext, PageProps } from "next";
import { useEffect } from "react";
import { graphql, usePreloadedQuery } from "react-relay";
import type { RelayComponent } from "relay-nextjs";
import { withRelay } from "relay-nextjs";

import type { AppBarVariant } from "~/components/app-bar/helpers";
import { AppFooter } from "~/components/app-footer";
import { DefaultCustomContents } from "~/components/article/article-render/consts";
import { FallbackLoading } from "~/components/fallback-loading";
import { SectionContextProvider } from "~/components/section/contexts";
import { SectionSeo } from "~/components/section/section-seo";
import { SectionStyle } from "~/components/section/section-style";
import { SectionStyleSeo } from "~/components/section/section-style/section-style-seo";
import { section as sectionData } from "~/data/section";
import { useTrackCurrentItem } from "~/lib/current-item/hooks";
import { createClientEnvironment } from "~/lib/relay/environment.client";
import { parseQueryString } from "~/lib/utils";
import { Variant } from "~/pages/section/enums";
import { getVariant } from "~/pages/section/helpers";
import type { sectionPageQuery } from "~/queries/__generated__/sectionPageQuery.graphql";

const query = graphql`
  query sectionPageQuery(
    $after: String
    $articlesQueueName: String!
    $customContents: [CustomContentInput]
    $entityId: String!
    $first: Int = 30
    $isSectionStyleMain: Boolean!
    $relatedSectionsQueueName: String!
  ) {
    section(filter: { entityId: $entityId }) {
      entityId
      name
      ...hooksTrackCurrentItemBase @arguments(customContents: $customContents)
      ...helpersSectionTheme
      topics {
        ...entityLink
        entityId
        name
      }
      ...contextsSectionContextProviderSection
      ...sectionSeoSection
    }

    # TODO: Differentiate Style Main/Sub
    ...sectionStyleContentQuery
      @arguments(
        after: $after
        articlesQueueName: $articlesQueueName
        entityId: $entityId
        first: $first
        isSectionStyleMain: $isSectionStyleMain
        relatedSectionsQueueName: $relatedSectionsQueueName
      )
  }
`;

const SectionPage: RelayComponent<unknown, sectionPageQuery> = ({ preloadedQuery }) => {
  const data = usePreloadedQuery(query, preloadedQuery);
  const { trackCurrentItem } = useTrackCurrentItem();
  useEffect(() => {
    trackCurrentItem(data.section);
  }, [data.section, trackCurrentItem]);

  // TODO: handle router.query.slide for main section
  const variant = getVariant(data.section);

  const handleRenderSection = () => {
    switch (variant) {
      case Variant.Style:
        return <SectionStyle reference={data} />;
      default:
        return (
          <>
            <div>
              <h1>{data.section.entityId}</h1>
              <h2>{data.section.name}</h2>
              <AppFooter />
            </div>
          </>
        );
    }
  };

  const handleRenderPageSeo = () => {
    switch (variant) {
      case Variant.Style:
        return <SectionStyleSeo reference={data.section} />;
      default:
        return <SectionSeo reference={data.section} />;
    }
  };

  // TODO: handle head meta seo
  return (
    <>
      {handleRenderPageSeo()}
      <SectionContextProvider reference={data.section}>
        {handleRenderSection()}
      </SectionContextProvider>
    </>
  );
};

SectionPage.displayName = "SectionPage";

const getPageProps = (context: NextPageContext) => {
  const hasAppBarAds = !!context.query.appBarAdStatus;
  const pageProps: PageProps = {
    appBarConfiguration: {
      adSlotVariants: {
        large: {
          height: 360,
          padding: 24,
        },
        small: {
          height: 100,
          padding: 10,
        },
      },
      hasDesktopAd: hasAppBarAds,
      hasMobileAd: hasAppBarAds,
      variant: context.query.appBarVariant as AppBarVariant,
    },
    contentConfiguration: {
      responsivePadding: {
        desktopUp: 12,
        tabletUp: 0,
      },
    },
    pageConfiguration: {
      responsivePadding: {
        tabletUp: 24,
      },
    },
  };

  return pageProps;
};
export default withRelay(SectionPage, query, {
  clientSideProps: context => getPageProps(context),
  createClientEnvironment: () => createClientEnvironment(),
  createServerEnvironment: async context => {
    const { createServerEnvironment } = await import("~/lib/relay/environment.server");
    return createServerEnvironment(context.req?.cookies);
  },
  fallback: <FallbackLoading />,
  serverSideProps: context => Promise.resolve(getPageProps(context)),
  variablesFromContext(context) {
    const entityId = parseQueryString(context.query.entityId) ?? "";

    const queueNames = {
      articlesQueueName: `section_top_${entityId}`,
      relatedSectionsQueueName:
        parseQueryString(context.query.relatedSectionsQueueName) ??
        `section_categories_${entityId}`,
    };

    return {
      ...queueNames,
      customContents: DefaultCustomContents,
      entityId,
      isSectionStyleMain: entityId === sectionData.style.entityId,
    };
  },
});
